import React, { useEffect, useMemo, useState } from "react";
import getWorkerCompliance from "../utils/compliance/getWorkerCompliance";
import { db, auth } from "../firebase";
import {
  collection,
  doc,
  serverTimestamp,
  updateDoc,
  addDoc,   // 👈 ADD THIS
} from "firebase/firestore";
import { isReadyForShifts } from "../modules/workforce";
import {
  calculateReliabilityScore,
  getReliabilityLabel,
} from "../modules/workforce";
import DepartmentLayout from "../enterprise/layouts/DepartmentLayout";
import EnterpriseHeader from "../enterprise/components/EnterpriseHeader";
import EnterpriseToolbar from "../enterprise/components/EnterpriseToolbar";
import EnterpriseMetricCard from "../enterprise/components/EnterpriseMetricCard";
import WorkforceSummary from "../modules/workforce/components/WorkforceSummary";
import WorkerRow from "../modules/workforce/components/WorkerRow.jsx";
import { buildWorkerMetricsFromShifts } from "../utils/buildWorkerMetrics";
import getWorkerOperationalState from "../utils/workers/getWorkerOperationalState";
import Worker360Overview from "../modules/workforce/components/Worker360Overview";
import Worker360Shifts from "../modules/workforce/components/Worker360Shifts";
import Worker360Payroll from "../modules/workforce/components/Worker360Payroll";
import Worker360Activity from "../modules/workforce/components/Worker360Activity";
import Worker360AIInsights from "../modules/workforce/components/Worker360AIInsights";

const normalizeId = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");

const normalizeTenantId = (value) => {
  const normalized = normalizeId(value);

  if (
    normalized === "dogebridge_staffing" ||
    normalized === "dogebridge_llc" ||
    normalized === "doge_bridge" ||
    normalized === "dogebridge"
  ) {
    return "dogebridge";
  }

  return normalized;
};

const workerBelongsToTenant = (worker, selectedTenant) => {
  const selected = normalizeTenantId(selectedTenant);

  // Platform-level view: do not restrict by tenant.
  if (!selected || selected === "all") {
    return true;
  }

  const workerTenant = normalizeTenantId(
    worker?.tenantId ||
      worker?.tenantKey ||
      worker?.tenantName ||
      ""
  );

  // Do not invent tenant membership.
  if (!workerTenant || workerTenant === "all") {
    return false;
  }

  return workerTenant === selected;
};

const workerBelongsToFacility = (worker, selectedFacility) => {
  const selected = normalizeId(selectedFacility);

  // Tenant-wide or platform-wide facility view.
  if (!selected || selected === "all") {
    return true;
  }

  const workerFacilityIds = [
    worker?.facilityId,
    worker?.primaryFacilityId,
    ...(Array.isArray(worker?.facilityIds)
      ? worker.facilityIds
      : []),
    ...(Array.isArray(worker?.allowedFacilityIds)
      ? worker.allowedFacilityIds
      : []),
  ]
    .map(normalizeId)
    .filter((id) => id && id !== "all");

  return workerFacilityIds.includes(selected);
};

function norm(v) {
  return String(v || "").trim().toLowerCase();
}

function titleCase(v) {
  return String(v || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function cardStyle() {
  return {
    border: "1px solid #d8e1ea",
    borderRadius: 14,
    background: "#fff",
    boxShadow: "0 2px 10px rgba(15,23,42,.04)",
  };
}

function inputStyle(extra = {}) {
  return {
    minHeight: 38,
    height: 38,
    padding: "0 12px",
    border: "1px solid #cbd5e1",
    borderRadius: 10,
    background: "#fff",
    fontSize: 13,
    fontWeight: 700,
    color: "#0f172a",
    outline: "none",
    ...extra,
  };
}

function buttonStyle(primary = false, extra = {}) {
  return {
    minHeight: 36,
    height: 36,
    padding: "0 12px",
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 900,
    cursor: "pointer",
    border: primary ? "1px solid #2563eb" : "1px solid #d8e1ea",
    background: primary ? "#2563eb" : "#fff",
    color: primary ? "#fff" : "#0f172a",
    ...extra,
  };
}

function badgeStyle(kind = "neutral") {
  if (kind === "green") {
    return {
      padding: "3px 8px",
      borderRadius: 999,
      background: "#ecfdf5",
      border: "1px solid #bbf7d0",
      color: "#166534",
      fontSize: 11,
      fontWeight: 900,
    };
  }

  if (kind === "amber") {
    return {
      padding: "3px 8px",
      borderRadius: 999,
      background: "#fff7ed",
      border: "1px solid #fdba74",
      color: "#9a3412",
      fontSize: 11,
      fontWeight: 900,
    };
  }

  if (kind === "blue") {
    return {
      padding: "3px 8px",
      borderRadius: 999,
      background: "#eff6ff",
      border: "1px solid #bfdbfe",
      color: "#1d4ed8",
      fontSize: 11,
      fontWeight: 900,
    };
  }

  return {
    padding: "3px 8px",
    borderRadius: 999,
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    color: "#475569",
    fontSize: 11,
    fontWeight: 800,
  };
}

function credentialsSummary(creds) {
  const arr = Array.isArray(creds) ? creds : [];
  const types = arr
    .map((c) => {
      if (typeof c === "string") return c;
      if (c && typeof c === "object") return c.type || c.credential || "";
      return "";
    })
    .map((x) => norm(x))
    .filter(Boolean);

  return [...new Set(types)];
}
function toMillis(v) {
  if (!v) return NaN;
  try {
    if (typeof v?.toDate === "function") return v.toDate().getTime();
    if (typeof v === "number") return v;
    return new Date(v).getTime();
  } catch {
    return NaN;
  }
}

function toDateInputValueFromAny(v) {
  const ms = toMillis(v);
  if (!Number.isFinite(ms)) return "";
  const d = new Date(ms);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${mm}/${dd}/${yyyy}`;
}

function parseMMDDYYYY(s) {
  const txt = String(s || "").trim();
  if (!txt) return null;
  const [mm, dd, yyyy] = txt.split("/").map((x) => x.trim());
  if (!mm || !dd || !yyyy) return null;

  const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd), 0, 0, 0, 0);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

function normalizeCreds(creds) {
  const arr = Array.isArray(creds) ? creds : [];

  const normalized = arr
    .map((c) => {
      if (typeof c === "string") {
        const type = norm(c);
        return type ? { type, verified: true, expiresAt: null } : null;
      }

      if (c && typeof c === "object") {
        const type = norm(c.type || c.credential || "");
        if (!type) return null;

        return {
          type,
          verified: c.verified === true,
          expiresAt: c.expiresAt ?? null,
        };
      }

      return null;
    })
    .filter(Boolean);

  const byType = new Map();

  for (const cred of normalized) {
    const key = norm(cred.type);
    const prev = byType.get(key);

    if (!prev) {
      byType.set(key, cred);
      continue;
    }

    const prevMs = toMillis(prev.expiresAt);
    const nextMs = toMillis(cred.expiresAt);

    const prevScore =
      (prev.verified ? 1000000000 : 0) + (Number.isFinite(prevMs) ? prevMs : 0);
    const nextScore =
      (cred.verified ? 1000000000 : 0) + (Number.isFinite(nextMs) ? nextMs : 0);

    if (nextScore >= prevScore) byType.set(key, cred);
  }

  return Array.from(byType.values()).sort((a, b) =>
    String(a.type || "").localeCompare(String(b.type || ""))
  );
}

function upsertCred(creds, nextCred) {
  const list = normalizeCreds(creds);
  const t = norm(nextCred.type);

  const filtered = list.filter((x) => norm(x.type) !== t);
  filtered.push({
    type: t,
    verified: nextCred.verified === true,
    expiresAt: nextCred.expiresAt ?? null,
  });

  filtered.sort((a, b) => String(a.type).localeCompare(String(b.type)));
  return filtered;
}

function deleteCredByType(creds, type) {
  const list = normalizeCreds(creds);
  const t = norm(type);
  return list.filter((x) => norm(x.type) !== t);
}
function DrawerCredRow({ cred, onSave, onDelete }) {
  const [draftType, setDraftType] = useState(norm(cred.type));
  const [draftVerified, setDraftVerified] = useState(cred.verified === true);
  const [draftExp, setDraftExp] = useState(toDateInputValueFromAny(cred.expiresAt));

  useEffect(() => {
    setDraftType(norm(cred.type));
    setDraftVerified(cred.verified === true);
    setDraftExp(toDateInputValueFromAny(cred.expiresAt));
  }, [cred]);


  const expLabel = draftExp ? `exp ${draftExp}` : "no expiry";
  

  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        borderRadius: 10,
        padding: 8,
        display: "grid",
        gap: 8,
        background: "#fff",
      }}
    >
      <div style={{ fontWeight: 900, fontSize: 12, color: "#0f172a" }}>
        {titleCase(cred.type)}{" "}
        <span style={{ color: "#64748b", fontWeight: 700 }}>
          — {draftVerified ? "Valid" : "Unverified"} ({expLabel})
        </span>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr auto auto",
          gap: 6,
          alignItems: "center",
        }}
      >
        <select
          value={draftType}
          onChange={(e) => setDraftType(e.target.value)}
          style={inputStyle({ width: "100%" })}
        >
          {["cna", "lvn", "rn", "med_aide"].map((x) => (
            <option key={x} value={x}>
              {titleCase(x)}
            </option>
          ))}
        </select>

        <input
          value={draftExp}
          onChange={(e) => setDraftExp(e.target.value)}
          placeholder="mm/dd/yyyy"
          style={inputStyle({ width: "100%" })}
        />

        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 12,
            fontWeight: 800,
            color: "#334155",
          }}
        >
          <input
            type="checkbox"
            checked={draftVerified}
            onChange={(e) => setDraftVerified(e.target.checked)}
          />
          Verified
        </label>

        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
          <button
            style={buttonStyle(true)}
            onClick={() =>
              onSave({
                type: draftType,
                verified: draftVerified,
                expTxt: draftExp,
              })
            }
          >
            Save
          </button>

          <button
            style={buttonStyle(false)}
            onClick={() => onDelete(cred.type)}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function WorkerManageDrawer({
  worker,
  facilities,
  shifts = [],
  initialSection = "overview",
  onClose,
  onSave,
  saving,
}) {

  const workerDocuments =
    worker?.credentialMap ||
    worker?.documents ||
    worker?.uploadedDocs ||
    worker?.credentials ||
    {};

  const documentRows = Object.entries(workerDocuments).map(([key, doc]) => ({
    key,
    label:
      doc?.label ||
      key.replaceAll("_", " ").replace(/\b\w/g, (m) => m.toUpperCase()),
    status: String(doc?.status || "missing").toLowerCase(),
    fileUrl: doc?.fileUrl || doc?.url || "",
    fileName: doc?.fileName || doc?.name || "",
  }));

  const reliabilityScore = calculateReliabilityScore(worker || {});
const reliabilityLabel = getReliabilityLabel(reliabilityScore);
const [activeSection, setActiveSection] = useState("overview");

  const [role, setRole] = useState(worker?.role || "worker");
  const [status, setStatus] = useState(worker?.status || "active");
  const [primaryFacility, setPrimaryFacility] = useState(worker?.facilityId || "");
  const [city, setCity] = useState(worker?.city || "");
const [state, setState] = useState(worker?.state || "TX");
const [zipCode, setZipCode] = useState(worker?.zipCode || "");
const [preferredRadiusMiles, setPreferredRadiusMiles] = useState(
  worker?.preferredRadiusMiles || 25
);
  const [allowedFacilities, setAllowedFacilities] = useState(
    Array.isArray(worker?.facilityIds) ? worker.facilityIds : worker?.facilityId ? [worker.facilityId] : []

  );

  const [draftCreds, setDraftCreds] = useState(normalizeCreds(worker?.credentials));
  const [addType, setAddType] = useState("cna");
  const [addExp, setAddExp] = useState("");

  useEffect(() => {
    if (!worker) return;

     setActiveSection(initialSection || "overview");
    setRole(worker.role || "worker");
    setStatus(worker.status || "active");
    setPrimaryFacility(worker.facilityId || "");
    setCity(worker.city || "");
    setState(worker.state || "TX");
    setZipCode(worker.zipCode || "");
    setPreferredRadiusMiles(worker.preferredRadiusMiles || 25);
    setAllowedFacilities(
      Array.isArray(worker.facilityIds)
        ? worker.facilityIds
        : worker.facilityId
        ? [worker.facilityId]
        : []
    );
    setDraftCreds(normalizeCreds(worker.credentials));
    setAddType("cna");
    setAddExp("");
  }, [worker, initialSection]);

  function toggleFacility(fid) {
    const id = norm(fid);
    setAllowedFacilities((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
    });
  }

  function handleCredentialSave(idx, draft) {
    const list = normalizeCreds(draftCreds);
    const current = list[idx];
    if (!current) return;

    const rest = list.filter((_, i) => i !== idx);
    const expDate = parseMMDDYYYY(draft.expTxt || "");
    const next = upsertCred(rest, {
      type: draft.type || current.type,
      verified: draft.verified === true,
      expiresAt: expDate || null,
    });

    setDraftCreds(next);
  }

  function handleCredentialDelete(type) {
    const ok = window.confirm(`Delete credential "${type}"?`);
    if (!ok) return;
    setDraftCreds((prev) => deleteCredByType(prev, type));
  }

  function handleAddCredential() {
    const expDate = parseMMDDYYYY(addExp || "");
    const next = upsertCred(draftCreds, {
      type: addType,
      verified: true,
      expiresAt: expDate || null,
    });
    setDraftCreds(next);
    setAddExp("");
  }

  function handleSetOnly() {
    const ok = window.confirm(
      `Set ONLY ${titleCase(addType)}?\n\nThis will remove all other credentials.`
    );
    if (!ok) return;

    setDraftCreds([
      {
        type: addType,
        verified: true,
        expiresAt: null,
      },
    ]);
  }

  function handleCopyCreds() {
    try {
      const txt = JSON.stringify(normalizeCreds(draftCreds), null, 2);

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(txt)
          .then(() => alert("Copied"))
          .catch(() => alert("Copy failed"));
      } else {
        window.prompt("Copy credentials JSON:", txt);
      }
    } catch (e) {
      console.error(e);
      alert("Copy failed");
    }
  }

  function handleSave() {
    const cleanedFacilities = [...new Set((allowedFacilities || []).map(norm).filter(Boolean))];
    const nextPrimary = norm(primaryFacility);

    const finalFacilities =
      nextPrimary && !cleanedFacilities.includes(nextPrimary)
        ? [nextPrimary, ...cleanedFacilities]
        : cleanedFacilities;

   const normalizedCreds = normalizeCreds(draftCreds);

const approvedPositions = normalizedCreds
  .filter((c) => c.verified)
  .map((c) => String(c.type || "").toUpperCase());

const primaryCredential =
  approvedPositions[0] ||
  String(addType || "").toUpperCase() ||
  null;

onSave({
  uid: worker.uid,

  role: norm(role),
  status: norm(status),

  facilityId: nextPrimary || null,
  facilityIds: finalFacilities,

  credentials: normalizedCreds,

  approvedPositions,
  positions: approvedPositions,

  workerType: primaryCredential,
  primaryRole: primaryCredential,
  roleType: primaryCredential,

  city: norm(city),
  state: String(state || "TX").trim().toUpperCase(),
  zipCode: String(zipCode || "").trim(),
  preferredRadiusMiles: Number(preferredRadiusMiles || 25),
});
  }

  if (!worker) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        right: 0,
        width: 460,
        maxWidth: "100%",
        height: "100vh",
        background: "#fff",
        borderLeft: "1px solid #d8e1ea",
        boxShadow: "-10px 0 30px rgba(15,23,42,.08)",
        zIndex: 1000,
        padding: 16,
        overflowY: "auto",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 10,
          marginBottom: 14,
        }}
      >
        <div>
          <div style={{ fontWeight: 900, fontSize: 18, color: "#0f172a" }}>
            Manage Worker
          </div>
          <div style={{ marginTop: 4, fontSize: 12, color: "#64748b", fontWeight: 700 }}>
            {worker.email || worker.uid}
          </div>
        </div>

        <button style={buttonStyle(false)} onClick={onClose}>
          Close
        </button>
      </div>

<div
  style={{
    display: "flex",
    gap: 6,
    marginBottom: 14,
    borderBottom: "1px solid #e2e8f0",
    paddingBottom: 10,
    overflowX: "auto",
  }}
>
  {[
    ["overview", "Overview"],
    ["profile", "Profile"],
    ["facility", "Facility"],
    ["compliance", "Compliance"],
    ["shifts", "Shifts"],
    ["payroll", "Payroll"],
    ["activity", "Activity"],
    ["ai", "AI"],
  ].map(([key, label]) => (
    <button
      key={key}
      type="button"
      onClick={() => setActiveSection(key)}
      style={{
        ...buttonStyle(activeSection === key),
        height: 34,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </button>
  ))}
</div>

<div style={{ display: "grid", gap: 14 }}>

{activeSection === "overview" && (
  <Worker360Overview
    worker={worker}
    shifts={shifts}
  />
)}

{activeSection === "shifts" && (
  <Worker360Shifts
    worker={worker}
    shifts={shifts}
  />
)}

{activeSection === "payroll" && (
  <Worker360Payroll
    worker={worker}
    shifts={shifts}
  />
)}

{activeSection === "activity" && (
  <Worker360Activity
    worker={worker}
    shifts={shifts}
  />
)}

{activeSection === "ai" && (
  <Worker360AIInsights
    worker={worker}
    shifts={shifts}
  />
)}

  {activeSection === "profile" ? (
    <>
      <div style={{ ...cardStyle(), padding: 12 }}>
        <div style={{ fontWeight: 900, fontSize: 14, color: "#0f172a" }}>
          Reliability
        </div>

    <div
      style={{
        marginTop: 8,
        fontSize: 28,
        fontWeight: 950,
        color: reliabilityScore >= 90 ? "#166534" : "#92400e",
      }}
    >
      {reliabilityScore}
    </div>

    <div style={{ marginTop: 4, color: "#64748b", fontWeight: 800 }}>
      {reliabilityLabel}
    </div>
  </div>

  <div style={{ ...cardStyle(), padding: 12 }}>
    <div style={{ fontWeight: 900, fontSize: 14, color: "#0f172a", marginBottom: 10 }}>
      Profile
    </div>

          <div style={{ display: "grid", gap: 10 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 900, color: "#64748b", marginBottom: 4 }}>
                Role
              </div>
              <select value={role} onChange={(e) => setRole(e.target.value)} style={inputStyle({ width: "100%" })}>
                <option value="worker">worker</option>
                <option value="planner">planner</option>
                <option value="admin">admin</option>
              </select>
            </div>

            <div>
              <div style={{ fontSize: 11, fontWeight: 900, color: "#64748b", marginBottom: 4 }}>
                Status
              </div>
              <select value={status} onChange={(e) => setStatus(e.target.value)} style={inputStyle({ width: "100%" })}>
                <option value="active">active</option>
                <option value="pending">pending</option>
                <option value="disabled">disabled</option>
              </select>
            </div>
          </div>
        </div>

        <div>
  <div style={{ fontSize: 11, fontWeight: 900, color: "#64748b", marginBottom: 4 }}>
    City
  </div>
  <input
    value={city}
    onChange={(e) => setCity(e.target.value)}
    placeholder="City"
    style={inputStyle({ width: "100%" })}
  />
</div>

<div>
  <div style={{ fontSize: 11, fontWeight: 900, color: "#64748b", marginBottom: 4 }}>
    State
  </div>
  <input
    value={state}
    onChange={(e) => setState(e.target.value.toUpperCase())}
    placeholder="State"
    style={inputStyle({ width: "100%" })}
  />
</div>

<div>
  <div style={{ fontSize: 11, fontWeight: 900, color: "#64748b", marginBottom: 4 }}>
    ZIP Code
  </div>
  <input
    value={zipCode}
    onChange={(e) => setZipCode(e.target.value)}
    placeholder="ZIP Code"
    style={inputStyle({ width: "100%" })}
  />
</div>

<div>
  <div style={{ fontSize: 11, fontWeight: 900, color: "#64748b", marginBottom: 4 }}>
    Preferred Radius Miles
  </div>
  <input
    type="number"
    value={preferredRadiusMiles}
    onChange={(e) => setPreferredRadiusMiles(Number(e.target.value || 0))}
    placeholder="25"
    style={inputStyle({ width: "100%" })}
  />
</div>
    </>
  ) : null}

  {activeSection === "facility" ? (
        <div style={{ ...cardStyle(), padding: 12 }}>
          <div style={{ fontWeight: 900, fontSize: 14, color: "#0f172a", marginBottom: 10 }}>
            Facility Membership
          </div>


          <div style={{ display: "grid", gap: 10 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 900, color: "#64748b", marginBottom: 4 }}>
                Primary Facility
              </div>
              <select
                value={primaryFacility}
                onChange={(e) => setPrimaryFacility(e.target.value)}
                style={inputStyle({ width: "100%" })}
              >
                <option value="">none</option>
                {facilities.map((f) => (
                  <option key={f.id || f.facilityId} value={norm(f.id || f.facilityId)}>
  {f.name || f.facilityName || f.id || f.facilityId}
</option>
                ))}
              </select>
            </div>

            <div>
              <div style={{ fontSize: 11, fontWeight: 900, color: "#64748b", marginBottom: 6 }}>
                Allowed Facilities
              </div>

              <div style={{ display: "grid", gap: 6 }}>
{(facilities || []).map((f) => {
  const fid = norm(f.id || f.facilityId);
  const name = f.name || f.facilityName || fid;
  const checked = allowedFacilities.includes(fid);

  return (
    <label key={fid}>
      <input
        type="checkbox"
        checked={checked}
        onChange={() => toggleFacility(fid)}
      />
      <span>{name}</span>
    </label>
  );
})}
              </div>
            </div>
            </div>
        </div>
  ) : null}

  {activeSection === "compliance" ? (
    <>
      <div style={{ ...cardStyle(), padding: 12 }}>
          <div style={{ fontWeight: 900, fontSize: 14, color: "#0f172a", marginBottom: 10 }}>
            Credentials & Documents
          </div>

          {documentRows.length === 0 ? (
            <div style={{ color: "#64748b", fontWeight: 800, fontSize: 12 }}>
              No documents uploaded.
            </div>
          ) : (
            <div style={{ display: "grid", gap: 8 }}>
              {documentRows.map((doc) => (
                <div
                  key={doc.key}
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 10,
                    padding: 10,
                    background: "#f8fafc",
                  }}
                >
                  <div style={{ fontWeight: 900 }}>{doc.label}</div>

                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                    {doc.fileName || "Uploaded document"} • {doc.status.toUpperCase()}
                  </div>

                  {doc.fileUrl ? (
                    <a
                      href={doc.fileUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        display: "inline-block",
                        marginTop: 6,
                        fontSize: 12,
                        fontWeight: 900,
                        color: "#2563eb",
                      }}
                    >
                      Open Document
                    </a>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ ...cardStyle(), padding: 12 }}>
          <div style={{ fontWeight: 900, fontSize: 14, color: "#0f172a", marginBottom: 10 }}>
            Credentials
          </div>

          <div style={{ display: "grid", gap: 8 }}>
            {draftCreds.length === 0 ? (
              <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>
                No credentials
              </div>
            ) : (
              draftCreds.map((cred, idx) => (
                <DrawerCredRow
                  key={`${worker.uid}-${cred.type}-${idx}`}
                  cred={cred}
                  onSave={(draft) => handleCredentialSave(idx, draft)}
                  onDelete={handleCredentialDelete}
                />
              ))
            )}

            <div
              style={{
                borderTop: "1px solid #e5e7eb",
                paddingTop: 10,
                display: "grid",
                gap: 8,
              }}
            >
              <div style={{ fontSize: 11, fontWeight: 900, color: "#64748b" }}>
                Add / Renew Credential
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr auto auto",
                  gap: 6,
                  alignItems: "center",
                }}
              >
                <select
                  value={addType}
                  onChange={(e) => setAddType(e.target.value)}
                  style={inputStyle()}
                >
                  {["cna", "lvn", "rn", "med_aide"].map((x) => (
                    <option key={x} value={x}>
                      {titleCase(x)}
                    </option>
                  ))}
                </select>

                <input
                  value={addExp}
                  onChange={(e) => setAddExp(e.target.value)}
                  placeholder="mm/dd/yyyy"
                  style={inputStyle()}
                />

                <button style={buttonStyle(true)} onClick={handleAddCredential}>
                  Add
                </button>

                <button style={buttonStyle(false)} onClick={handleSetOnly}>
                  Set only
                </button>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-start" }}>
                <button style={buttonStyle(false)} onClick={handleCopyCreds}>
                  Copy creds
                </button>
              </div>
            </div>
              </div>
        </div>
    </>
  ) : null}

{["profile", "facility", "compliance"].includes(activeSection) && (
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button
              type="button"
              style={buttonStyle(false)}
              onClick={onClose}
            >
              Cancel
            </button>

            <button
              type="button"
              style={buttonStyle(true)}
              disabled={saving}
              onClick={handleSave}
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
export default function AdminWorkforceManager({
  tenantId = "dogebridge",
  facilityId,
  activeContext,
  externalUsers = null,
  externalFacilities = null,
  shifts = [],
  attentionTarget = "",
  onApproveInvitedUser = null,
}) {

 useEffect(() => {
  if (!attentionTarget) return;

  const worker = (externalUsers || []).find(
    (u) =>
      String(u.uid || u.id || "").toLowerCase() ===
      String(attentionTarget || "").toLowerCase()
  );

  if (worker) {
    setSelectedWorker(worker);
  }

  setTimeout(() => {
    const el = document.getElementById("workforce-attention-worker");

    if (el) {
      el.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, 250);
}, [attentionTarget, externalUsers]);

  const [users, setUsers] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [facilityFilter, setFacilityFilter] = useState(
  normalizeId(facilityId) || "all"
);
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [workerDrawerSection, setWorkerDrawerSection] = useState("overview");
  const [savingWorker, setSavingWorker] = useState(false);

  const selectedFacilityRecord = useMemo(() => {
  const selected = normalizeId(facilityFilter);

  if (!selected || selected === "all") {
    return null;
  }

  return (facilities || []).find((facility) => {
    const id = normalizeId(
      facility.id ||
        facility.facilityId
    );

    return id === selected;
  }) || null;
}, [facilities, facilityFilter]);

const tenantDisplayName = useMemo(() => {
  const selected = normalizeTenantId(tenantId);

  if (!selected || selected === "all") {
    return "All Tenants";
  }

  if (selected === "dogebridge") {
    return "DogeBridge Staffing";
  }

  return titleCase(selected);
}, [tenantId]);

const facilityDisplayName = useMemo(() => {
  const selected = normalizeId(facilityFilter);

  if (!selected || selected === "all") {
    return "All Facilities";
  }

  return (
    selectedFacilityRecord?.name ||
    selectedFacilityRecord?.facilityName ||
    titleCase(selected)
  );
}, [facilityFilter, selectedFacilityRecord]);

 useEffect(() => {
  setFacilityFilter(normalizeId(facilityId) || "all");
}, [facilityId]);

  // Enterprise owns the live, tenant-scoped subscriptions. Reuse that data
  // rather than issuing a second, unfiltered query from the child screen.
  useEffect(() => {
    setFacilities(Array.isArray(externalFacilities) ? externalFacilities : []);
    setUsers(Array.isArray(externalUsers) ? externalUsers : []);
    setLoading(false);
  }, [externalFacilities, externalUsers]);
  const filteredUsers = useMemo(() => {
  const q = norm(search);

const usingExternalUsers = Array.isArray(externalUsers);

const sourceUsers = (
  usingExternalUsers
    ? externalUsers
    : users
).map((worker) => {

  const normalizedFacilityIds = [
    worker?.facilityId,
    worker?.primaryFacilityId,
    ...(Array.isArray(worker?.facilityIds)
      ? worker.facilityIds
      : []),
    ...(Array.isArray(worker?.allowedFacilityIds)
      ? worker.allowedFacilityIds
      : []),
  ]
    .map(normalizeId)
    .filter((id) => id && id !== "all");

  return {
    ...worker,

    tenantId: normalizeTenantId(
      worker?.tenantId ||
        worker?.tenantKey ||
        worker?.tenantName ||
        ""
    ),

    facilityId: normalizeId(
      worker?.facilityId ||
        worker?.primaryFacilityId ||
        ""
    ),

    facilityIds: [...new Set(normalizedFacilityIds)],
  };
});


  return (sourceUsers || [])
    .filter((u) => {
      if (u.archived === true) return false;

      const email = norm(u.email || u.uid);
      const role = norm(u.role);
      const status = norm(u.status);

     // The parent dashboard already applies the tenant scope to externalUsers.
// Only apply this local tenant check when this component loaded users itself.
if (
  !usingExternalUsers &&
  !workerBelongsToTenant(u, tenantId)
) {
  return false;
}

      if (!workerBelongsToFacility(u, facilityFilter)) {
        return false;
      }

      if (q && !email.includes(q)) return false;

      if (
        roleFilter !== "all" &&
        role !== roleFilter
      ) {
        return false;
      }

      if (
        statusFilter !== "all" &&
        status !== statusFilter
      ) {
        return false;
      }

      return true;
    })
    .sort((a, b) =>
      String(a.email || a.uid).localeCompare(
        String(b.email || b.uid)
      )
    );
}, [
  externalUsers,
  users,
  search,
  roleFilter,
  statusFilter,
  facilityFilter,
  tenantId,
]);

const workforceUsers = useMemo(() => {
  const byEmail = new Map();
const eligibleWorkers = (filteredUsers || []).filter(
  (u) => getWorkerOperationalState(u).ready === true
);

  eligibleWorkers.forEach((u) => {
    const email = String(u.email || u.uid || "").trim().toLowerCase();
    if (!email) return;

   const op = getWorkerOperationalState(u);

const score = op.ready === true ? 10 : 1;

const existing = byEmail.get(email);

if (!existing || score > existing.score) {
  byEmail.set(email, { user: u, score });
}
  });

  return Array.from(byEmail.values())
    .map((x) => x.user)
    .sort((a, b) =>
      String(a.email || a.uid || "").localeCompare(
        String(b.email || b.uid || "")
      )
    );
}, [filteredUsers]);


async function handleSaveWorker(next) {
  try {
    setSavingWorker(true);

    const cleanedFacilityIds = [...new Set((next.facilityIds || []).map(norm).filter(Boolean))];
    const cleanedPrimary = norm(next.facilityId);

    const finalFacilityIds =
      cleanedPrimary && !cleanedFacilityIds.includes(cleanedPrimary)
        ? [cleanedPrimary, ...cleanedFacilityIds]
        : cleanedFacilityIds;

    const cleanedCreds = normalizeCreds(next.credentials);

  await updateDoc(doc(db, "users", next.uid), {
  role: norm(next.role),
  status: norm(next.status),

  workerStatus: "ready",
  workerOperationalState: "READY",
  onboardingStatus: "approved",
  onboardingCompleted: true,
  complianceStatus: "approved",

  tenantId: normalizeTenantId(
    next.tenantId ||
      tenantId ||
      ""
  ),

  tenantName:
    next.tenantName ||
    "",

  facilityId: cleanedPrimary || null,
  facilityIds: finalFacilityIds,

  credentials: cleanedCreds,

  approvedPositions: next.approvedPositions || [],
  positions: next.positions || [],

  workerType: next.workerType || null,
  primaryRole: next.primaryRole || null,
  roleType: next.roleType || null,

  city: norm(next.city),
  state: String(next.state || "TX").trim().toUpperCase(),
  zipCode: String(next.zipCode || "").trim(),
  preferredRadiusMiles: Number(next.preferredRadiusMiles || 25),

  updatedAt: serverTimestamp(),
  updatedBy: auth.currentUser?.email || null,
});

    await addDoc(collection(db, "adminLogs"), {
  action: "update_worker",
  targetUid: next.uid,
  changes: {
    role: next.role,
    status: next.status,
    facilityId: next.facilityId,
    facilityIds: next.facilityIds,
  },
  performedBy: auth.currentUser?.email || null,
  createdAt: serverTimestamp(),
});

 setUsers((prev) =>
  (prev || []).map((u) =>
    u.uid === next.uid
      ? {
          ...u,
          role: norm(next.role),
          status: norm(next.status),

          workerStatus: "ready",
          workerOperationalState: "READY",
          onboardingStatus: "approved",
          onboardingCompleted: true,
          complianceStatus: "approved",

        tenantId: normalizeTenantId(
          next.tenantId ||
          tenantId ||
          ""
        ),

        tenantName:
          next.tenantName ||
          "",

          facilityId: cleanedPrimary || null,
          facilityIds: finalFacilityIds,
          credentials: cleanedCreds,
        }
      : u
  )
);

setSelectedWorker((prev) =>
  prev && prev.uid === next.uid
    ? {
        ...prev,
        role: norm(next.role),
        status: norm(next.status),

        workerStatus: "ready",
        workerOperationalState: "READY",
        onboardingStatus: "approved",
        onboardingCompleted: true,
        complianceStatus: "approved",

      tenantId: normalizeTenantId(
        next.tenantId ||
        tenantId ||
        ""
      ),

      tenantName:
        next.tenantName ||
        "",

        facilityId: cleanedPrimary || null,
        facilityIds: finalFacilityIds,
        credentials: cleanedCreds,
      }
    : prev
);

    

    alert("Worker updated successfully.");
  } catch (e) {
    console.error("handleSaveWorker error:", e);
    alert(e?.message || "Failed to update worker");
  } finally {
    setSavingWorker(false);
  }
}

return (
  <DepartmentLayout
    header={
      <EnterpriseHeader
        title="Workforce Manager"
        subtitle="Search, review, and safely correct worker assignments and profile state."
      />
    }
  >
    <WorkforceSummary workforceUsers={workforceUsers} />

    <div style={{ ...cardStyle(), padding: 14 }}>

        <div style={{ marginTop: 4, fontSize: 12, color: "#64748b", fontWeight: 700 }}>
          Search, review, and safely correct worker assignments and profile state.
        </div>

<EnterpriseToolbar
  searchValue={search}
  onSearchChange={setSearch}
  searchPlaceholder="Search worker..."
>
  <select
    value={facilityFilter}
    onChange={(e) => setFacilityFilter(e.target.value)}
    style={inputStyle({ width: "100%" })}
  >
    <option value="all">All facilities</option>

    {facilities.map((f) => (
      <option
        key={f.id || f.facilityId}
        value={norm(f.id || f.facilityId)}
      >
        {f.name || f.facilityName || f.id || f.facilityId}
      </option>
    ))}
  </select>

  <select
    value={roleFilter}
    onChange={(e) => setRoleFilter(e.target.value)}
    style={inputStyle({ width: "100%" })}
  >
    <option value="all">All roles</option>
    <option value="worker">Worker</option>
    <option value="planner">Planner</option>
    <option value="admin">Admin</option>
  </select>

  <select
    value={statusFilter}
    onChange={(e) => setStatusFilter(e.target.value)}
    style={inputStyle({ width: "100%" })}
  >
    <option value="all">All statuses</option>
    <option value="active">Active</option>
    <option value="pending">Pending</option>
    <option value="disabled">Disabled</option>
  </select>
</EnterpriseToolbar>
      </div>

      <div style={{ ...cardStyle(), padding: 12 }}>
        <div
          style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            marginBottom: 10,
          }}
        >
       <span style={badgeStyle("blue")}>
  Tenant: {tenantDisplayName}
</span>

<span style={badgeStyle("green")}>
  Facility: {facilityDisplayName}
</span>

<span style={badgeStyle()}>
  {(activeContext?.tab || "workforce")} •{" "}
  {(activeContext?.mode || "manual")}
</span>

<span style={badgeStyle()}>
  {workforceUsers.length} visible worker
  {workforceUsers.length === 1 ? "" : "s"}
</span>
        </div>

        {loading ? (
          <div style={{ fontSize: 13, color: "#64748b", fontWeight: 800 }}>
            Loading workforce…
          </div>
        ) : workforceUsers.length === 0 ? (
          <div style={{ fontSize: 13, color: "#64748b", fontWeight: 800 }}>
            No workers found.
          </div>
        ) : (
          <div style={{ display: "grid", gap: 8 }}>

{workforceUsers.map((u) => (
  <WorkerRow
    key={u.uid}
    worker={{
      ...u,
      ...buildWorkerMetricsFromShifts(u.uid || u.id, shifts),
    }}
    attentionTarget={attentionTarget}
    onApproveInvitedUser={onApproveInvitedUser}

   onManageWorker={(worker) => {
  setWorkerDrawerSection("overview");
  setSelectedWorker(worker);
}}
    onTransferFacility={(worker) => {
      setWorkerDrawerSection("facility");
      setSelectedWorker(worker);
    }}
  />
))}
          </div>
        )}
      </div>

      <WorkerManageDrawer
        worker={selectedWorker}
        facilities={facilities}
        shifts={shifts}
        initialSection={workerDrawerSection}
        onClose={() => setSelectedWorker(null)}
        onSave={handleSaveWorker}
        saving={savingWorker}
      />
    </DepartmentLayout>
  );
}
